import React, { useEffect, useMemo, useState } from 'react';
import './Dashboard.css';
import ManagerKPIDashboard from './ManagerKPIDashboard';
import KPICharts from './KPICharts';
import AttendanceStatus from './AttendanceStatus';
import VacationTracker from './VacationTracker';
import Scorecard from './Scorecard';
import Button from './ui/Button';
import SkeletonBlock from './ui/SkeletonBlock';
import Avatar from './ui/Avatar';
import { calculateRollups } from '../utils/calculations';
import { apiRequest } from '../services/apiClient';
import { useFetch } from '../hooks/useFetch';
import { buildQuery } from '../utils/buildQuery';
import { createDefaultPerformanceContext, derivePeriodStartFromContext } from './director/performanceRows';
import {
  pickDrivers,
  buildHeadlineNarrative,
  buildRiskPatterns,
  buildCoachingTopics,
  computePeriodDelta
} from '../utils/scorecardInsights';
import KpiCardUI from './ui/KpiCard';
import EmptyState from './ui/EmptyState';
import { StatusDot } from './ui/Status';
import CoachingLog from './coaching/CoachingLog';
import { useAuth } from '../context/AuthContext';
import {
  DIRECTOR_DATA_UPDATED_EVENT,
  DIRECTOR_SCORECARD_LAYOUT_UPDATED_EVENT
} from '../utils/directorEvents';

const DIRECTOR_LOCATION_TOTAL_ID_PREFIX = '__director_location_total__';

function getStoredScorecardCardLimit() {
  try {
    const value = Number(window.localStorage.getItem('directorScorecardCardLimit'));
    if (Number.isFinite(value)) {
      return Math.max(1, Math.min(8, Math.trunc(value)));
    }
  } catch (error) {
    // ignore storage issues and fall back to default
  }

  return 4;
}

function buildTrackerKpiMap(metrics = []) {
  return metrics.reduce((accumulator, metric) => {
    const actualValue = metric.actual ?? null;
    const goalValue = metric.goal ?? null;

    accumulator[metric.kpiKey] = {
      kpiKey: metric.kpiKey,
      label: metric.label,
      actualValue,
      goalValue,
      current: actualValue,
      target: goalValue,
      unit: metric.unit || '',
      variance: metric.variance,
      percentToGoal: metric.percentToGoal,
      rollingProgress: metric.rollingProgress,
      elapsedPercent: metric.elapsedPercent,
      status: metric.status,
      isHigherBetter: metric.isHigherBetter
    };
    return accumulator;
  }, {});
}

function buildSnapshotChartData(metrics = []) {
  return metrics.reduce((accumulator, metric) => {
    accumulator[metric.kpiKey] = {
      Goal: metric.goal ?? 0,
      Actual: metric.actual ?? 0
    };
    return accumulator;
  }, {});
}

// `sortDashboardKpis`, `selectExecutiveMetrics`, `selectExecutiveChartMetrics`
// were removed in Batch 1 / B15 along with the classic KPI overview accordion
// that consumed them. They can be reintroduced from git history if a future
// surface wants a curated top-N preview.

function deriveDateRangeFromPeriodContext(periodType, periodStart) {
  if (!periodStart) {
    return { startDate: '', endDate: '' };
  }

  const start = new Date(`${periodStart}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) {
    return { startDate: '', endDate: '' };
  }

  if (periodType === 'weekly') {
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    return {
      startDate: periodStart,
      endDate: end.toISOString().slice(0, 10)
    };
  }

  if (periodType === 'monthly') {
    const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0));
    return {
      startDate: periodStart,
      endDate: end.toISOString().slice(0, 10)
    };
  }

  if (periodType === 'quarterly') {
    const quarterStartMonth = Math.floor(start.getUTCMonth() / 3) * 3;
    const end = new Date(Date.UTC(start.getUTCFullYear(), quarterStartMonth + 3, 0));
    return {
      startDate: periodStart,
      endDate: end.toISOString().slice(0, 10)
    };
  }

  return {
    startDate: periodStart,
    endDate: `${start.getUTCFullYear()}-12-31`
  };
}

function buildTrackerRequestPath({ isDirectorLocationTotal, manager, managerData, periodType, periodStart }) {
  if (isDirectorLocationTotal) {
    const locationCode = managerData?.locationCode;
    const qs = buildQuery({
      periodType,
      periodStart,
      locationCode: locationCode && locationCode !== 'ALL' ? locationCode : null
    });
    return `/api/tracker/location?${qs}`;
  }
  const qs = buildQuery({ periodType, periodStart });
  return `/api/tracker/manager/${encodeURIComponent(manager)}?${qs}`;
}

function buildScorecardActualScope({ isDirectorLocationTotal, manager, managerData, locationCode }) {
  if (isDirectorLocationTotal) {
    return locationCode ? { locationCode } : {};
  }

  const managerId = Number(managerData?.managerDbId ?? managerData?.id ?? manager);
  return Number.isFinite(managerId) && managerId > 0 ? { managerId } : {};
}

function buildScorecardSupportCounts(approvalGroups = []) {
  return approvalGroups.reduce((summary, group) => {
    (group.items || []).forEach((item) => {
      if (item.status !== 'pending') {
        return;
      }

      summary.totalPending += 1;
      summary.byDomain[item.approvalDomain] = (summary.byDomain[item.approvalDomain] || 0) + 1;
    });
    return summary;
  }, {
    totalPending: 0,
    byDomain: {
      flagged_actual: 0,
      goal: 0,
      attendance_event: 0,
      performance_impact: 0
    }
  });
}

function getScorecardAggregationMode(kpi = {}) {
  const joined = `${kpi.kpiKey || ''} ${kpi.label || ''}`.toLowerCase();
  const formatType = String(kpi.formatType || '').toLowerCase();
  const unit = String(kpi.unit || '').toLowerCase();
  const kpiKey = String(kpi.kpiKey || '').trim();

  if ([
    'laborEfficiency',
    'serviceResponseTime',
    'customerSatisfaction',
    'qualityScore',
    'attendanceReliability',
    'teamProductivity',
    'goalCompletionRate'
  ].includes(kpiKey)) {
    return 'average';
  }

  if (formatType === 'percent' || formatType === 'points' || unit === '%' || unit === 'pts') {
    return 'average';
  }

  if (
    joined.includes('per ')
    || joined.includes('cycle time')
    || joined.includes('cost per')
    || joined.includes('share')
    || joined.includes('utilization')
    || joined.includes('advocacy')
    || joined.includes('readiness')
    || joined.includes('completion')
    || joined.includes('coverage')
    || joined.includes('compliance')
  ) {
    return 'average';
  }

  if (joined.includes('sales') || joined.includes('revenue')) {
    return 'sum';
  }

  return formatType === 'number' ? 'sum' : 'average';
}

function summarizeScorecardActualRowsByKpi(actualRows = [], kpis = []) {
  const kpiById = new Map((kpis || []).map((kpi) => [Number(kpi.id), kpi]));
  const groupedValues = new Map();

  (actualRows || []).forEach((actual) => {
    const kpiDefinitionId = Number(actual?.kpiDefinitionId);
    const actualValue = Number(actual?.actualValue);

    if (!Number.isFinite(kpiDefinitionId) || !Number.isFinite(actualValue)) {
      return;
    }

    if (!groupedValues.has(kpiDefinitionId)) {
      groupedValues.set(kpiDefinitionId, []);
    }

    groupedValues.get(kpiDefinitionId).push(actualValue);
  });

  return Array.from(groupedValues.entries()).reduce((summary, [kpiDefinitionId, values]) => {
    const kpi = kpiById.get(kpiDefinitionId) || {};
    const aggregationMode = getScorecardAggregationMode(kpi);
    const resolvedValue = aggregationMode === 'sum'
      ? values.reduce((sum, value) => sum + value, 0)
      : values.reduce((sum, value) => sum + value, 0) / values.length;

    summary.set(kpiDefinitionId, resolvedValue);
    return summary;
  }, new Map());
}

function buildScorecardHistoryByKpi(actualRows = [], kpis = []) {
  const kpiById = new Map((kpis || []).map((kpi) => [Number(kpi.id), kpi]));
  const groupedValues = new Map();

  (actualRows || []).forEach((actual) => {
    const kpiDefinitionId = Number(actual?.kpiDefinitionId);
    const actualValue = Number(actual?.actualValue);
    const periodKey = actual?.periodStart
      || actual?.actualDate
      || [actual?.actualYear, actual?.actualQuarter, actual?.actualMonth, actual?.actualWeek].filter(Boolean).join('-');

    if (!Number.isFinite(kpiDefinitionId) || !Number.isFinite(actualValue) || !periodKey) {
      return;
    }

    if (!groupedValues.has(kpiDefinitionId)) {
      groupedValues.set(kpiDefinitionId, new Map());
    }

    const periodValues = groupedValues.get(kpiDefinitionId);
    if (!periodValues.has(periodKey)) {
      periodValues.set(periodKey, []);
    }

    periodValues.get(periodKey).push(actualValue);
  });

  return Array.from(groupedValues.entries()).reduce((summary, [kpiDefinitionId, periodValues]) => {
    const kpi = kpiById.get(kpiDefinitionId) || {};
    const aggregationMode = getScorecardAggregationMode(kpi);
    const history = Array.from(periodValues.entries())
      .sort(([leftPeriod], [rightPeriod]) => String(leftPeriod).localeCompare(String(rightPeriod)))
      .map(([, values]) => (
        aggregationMode === 'sum'
          ? values.reduce((sum, value) => sum + value, 0)
          : values.reduce((sum, value) => sum + value, 0) / values.length
      ))
      .slice(-12);

    summary.set(kpiDefinitionId, history);
    return summary;
  }, new Map());
}

function computeScorecardAchievementPercent({ target, result, isHigherBetter }) {
  if (!Number.isFinite(target) || !Number.isFinite(result) || target === 0) {
    return null;
  }

  if (isHigherBetter) {
    return Math.max(0, (result / target) * 100);
  }

  if (result === 0) {
    return 100;
  }

  return Math.max(0, (target / result) * 100);
}

function computeScorecardStatusTone(achievementPercent, metricStatus) {
  if (Number.isFinite(achievementPercent)) {
    if (achievementPercent >= 105) {
      return 'good';
    }
    if (achievementPercent >= 95) {
      return 'fair';
    }
    return 'warning';
  }

  if (metricStatus === 'ahead') {
    return 'good';
  }
  if (metricStatus === 'on_track' || metricStatus === 'partial') {
    return 'fair';
  }
  if (metricStatus === 'behind') {
    return 'warning';
  }
  return 'neutral';
}

function computeScorecardStatusLabel(statusTone, hasPartialData = false) {
  if (statusTone === 'good') {
    return 'Ahead';
  }
  if (statusTone === 'fair') {
    return hasPartialData ? 'Partial Data' : 'Watch';
  }
  if (statusTone === 'warning') {
    return 'Needs Attention';
  }
  return 'Pending Data';
}

function buildScorecardSummary(rows = []) {
  const scoredRows = (rows || []).filter((row) => Number.isFinite(row.achievementPercent));
  const overallScore = scoredRows.length
    ? Math.round(scoredRows.reduce((sum, row) => sum + Math.min(row.achievementPercent, 100), 0) / scoredRows.length)
    : 0;

  return {
    overallScore
  };
}

function buildScorecardRows({
  metrics = [],
  // alternateMetrics no longer used after the v2 trend-column refactor;
  // accepted as a kwarg for caller backward-compat, dropped in the body.
  alternateMetrics: _alternateMetrics = [],   // eslint-disable-line no-unused-vars
  kpis = [],
  approvalGroups = [],
  actualRows = [],
  locationOwner = null,
  locationCode = '',
  locationName = '',
  managerId = null
}) {
  const kpiMap = new Map((kpis || []).map((kpi) => [kpi.kpiKey, kpi]));
  const actualSummaryByKpiId = summarizeScorecardActualRowsByKpi(actualRows, kpis);
  const actualHistoryByKpiId = buildScorecardHistoryByKpi(actualRows, kpis);
  const approvalCountsByKpi = {};

  approvalGroups.forEach((group) => {
    (group.items || []).forEach((item) => {
      const kpiKey = item?.metadata?.kpiKey || null;
      if (!kpiKey) {
        return;
      }

      if (!approvalCountsByKpi[kpiKey]) {
        approvalCountsByKpi[kpiKey] = {
          goal: 0,
          goal_proposal: 0,
          flagged_actual: 0
        };
      }

      if (item.approvalDomain === 'goal' || item.approvalDomain === 'goal_proposal' || item.approvalDomain === 'flagged_actual') {
        approvalCountsByKpi[kpiKey][item.approvalDomain] += 1;
      }
    });
  });

  const rows = (metrics || []).map((metric) => {
    const kpi = kpiMap.get(metric.kpiKey) || {};
    const approvalCounts = approvalCountsByKpi[metric.kpiKey] || { goal: 0, goal_proposal: 0, flagged_actual: 0 };
    const actualOverride = actualSummaryByKpiId.get(Number(kpi.id));
    const actualHistory = actualHistoryByKpiId.get(Number(kpi.id)) || [];
    const result = Number.isFinite(actualOverride) ? actualOverride : metric.actual;
    const percentToGoal = computeScorecardAchievementPercent({
      target: metric.goal,
      result,
      isHigherBetter: metric.isHigherBetter ?? kpi.isHigherBetter ?? true
    });
    // v2 Phase 2 — period-over-period delta from the actual-history series.
    // Old behavior compared MTD% to YTD% (alternateMetric), which read as
    // "is monthly pacing different from yearly?" — useful pacing signal but
    // not what a coach needs. The new comparison uses last actual vs.
    // second-to-last actual from the same view, answering "is this getting
    // better or worse than last period?" The alternateMetric is still
    // available if any caller wants it later, but no longer drives the
    // trend label.
    const isHigherBetter = metric.isHigherBetter ?? kpi.isHigherBetter ?? true;
    const isPercentKpi = String(metric.unit || kpi.unit || '').trim() === '%';
    let trendDelta = null;
    if (actualHistory.length >= 2) {
      const lastVal = actualHistory[actualHistory.length - 1];
      const priorVal = actualHistory[actualHistory.length - 2];
      if (Number.isFinite(lastVal) && Number.isFinite(priorVal)) {
        if (isPercentKpi) {
          trendDelta = lastVal - priorVal; // pp
        } else if (priorVal !== 0) {
          trendDelta = ((lastVal - priorVal) / priorVal) * 100; // %
        }
      }
    }
    // For lower-better KPIs, invert the performance direction so "down on
    // a cycle-time KPI" reads as improvement.
    const trendPerformanceDelta = (trendDelta != null && !isHigherBetter)
      ? -trendDelta
      : trendDelta;

    const statusTone = computeScorecardStatusTone(percentToGoal, metric.status);
    const statusLabel = computeScorecardStatusLabel(
      statusTone,
      !Number.isFinite(percentToGoal) && (Number.isFinite(metric.goal) || Number.isFinite(result))
    );

    let trendDirection = 'flat';
    let trendLabel = 'No comparison';
    if (trendPerformanceDelta !== null) {
      const unit = isPercentKpi ? 'pp' : '%';
      const stable = 1; // |Δ| < 1 pp/% reads as Stable
      if (trendPerformanceDelta >= stable) {
        trendDirection = 'up';
        trendLabel = `+${Math.abs(trendDelta).toFixed(1)} ${unit} vs. last period`;
      } else if (trendPerformanceDelta <= -stable) {
        trendDirection = 'down';
        trendLabel = `${trendDelta < 0 ? '' : '-'}${Math.abs(trendDelta).toFixed(1)} ${unit} vs. last period`;
      } else {
        trendLabel = 'Stable';
      }
    }

    const contextItems = [];
    const goalApprovalCount = (approvalCounts.goal || 0) + (approvalCounts.goal_proposal || 0);
    if (goalApprovalCount > 0) {
      contextItems.push({
        tone: 'fair',
        label: `${goalApprovalCount} goal ${goalApprovalCount === 1 ? 'approval' : 'approvals'}`
      });
    }
    if (approvalCounts.flagged_actual > 0) {
      contextItems.push({
        tone: 'warning',
        label: `${approvalCounts.flagged_actual} flagged ${approvalCounts.flagged_actual === 1 ? 'actual' : 'actuals'}`
      });
    }
    contextItems.push({
      tone: locationOwner?.name ? 'neutral' : 'warning',
      label: locationOwner?.name ? `Owner ${locationOwner.name}` : 'Owner unassigned'
    });

    return {
      kpiKey: metric.kpiKey,
      label: metric.label,
      category: metric.category || kpi.category || 'General',
      locationCode: metric.locationCode || locationCode || '',
      locationName: metric.locationName || locationName || locationCode || '',
      managerId: metric.managerId || managerId || null,
      goalLabel: metric.sectionName || kpi.sectionName || metric.category || kpi.category || 'Operational Goal',
      unit: metric.unit || kpi.unit || '',
      target: metric.goal,
      result,
      achievementPercent: percentToGoal,
      statusTone,
      statusLabel,
      trendDirection,
      trendLabel,
      history: actualHistory.length ? actualHistory : (Number.isFinite(result) ? [result] : []),
      contextItems,
      hasPendingGoalApproval: goalApprovalCount > 0,
      hasFlaggedActual: approvalCounts.flagged_actual > 0
    };
  });

  const underperforming = rows
    .filter((row) => row.statusTone === 'warning')
    .sort((left, right) => (left.achievementPercent ?? Infinity) - (right.achievementPercent ?? Infinity))
    .slice(0, 3);
  const biggestDrivers = rows
    .filter((row) => Number.isFinite(row.achievementPercent))
    .sort((left, right) => (right.achievementPercent ?? -Infinity) - (left.achievementPercent ?? -Infinity))
    .slice(0, 3);

  return {
    rows,
    summary: buildScorecardSummary(rows),
    insights: {
      underperforming,
      biggestDrivers
    }
  };
}

function Dashboard({
  data,
  period,
  manager,
  dataVersion,
  variant = 'standard',
  scorecardContext = null,
  scorecardCardLimit = null,
  onManagerDrilldown = null,
  onNavigate = null
}) {
  const { user: currentUser } = useAuth();
  const isDirectorLocationTotal = String(manager).startsWith(DIRECTOR_LOCATION_TOTAL_ID_PREFIX);
  const [trackerPayload, setTrackerPayload] = useState(null);
  const [trackerError, setTrackerError] = useState('');
  const [isTrackerLoading, setIsTrackerLoading] = useState(false);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [attendanceError, setAttendanceError] = useState('');
  const [isAttendanceLoading, setIsAttendanceLoading] = useState(false);
  const [lastTrackerRefreshAt, setLastTrackerRefreshAt] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [screenScorecardData, setScreenScorecardData] = useState(null);
  const [screenScorecardError, setScreenScorecardError] = useState('');
  const [screenScorecardViewMode, setScreenScorecardViewMode] = useState(period === 'yearly' ? 'ytd' : 'mtd');
  const [goalProposalModalOpen, setGoalProposalModalOpen] = useState(false);
  const [goalProposalKpiKey, setGoalProposalKpiKey] = useState('');
  const [goalProposalValue, setGoalProposalValue] = useState('');
  const [goalProposalReason, setGoalProposalReason] = useState('');
  const [goalProposalError, setGoalProposalError] = useState('');
  const [goalProposalBanner, setGoalProposalBanner] = useState('');
  const [isSubmittingGoalProposal, setIsSubmittingGoalProposal] = useState(false);
  const [scorecardCardLimitState, setScorecardCardLimitState] = useState(() => (
    Number.isFinite(Number(scorecardCardLimit))
      ? Math.max(1, Math.min(8, Math.trunc(Number(scorecardCardLimit))))
      : getStoredScorecardCardLimit()
  ));
  const hasManagerData = Boolean(data && data.managers && manager);
  const managerData = hasManagerData
    ? (data.managers[manager] || null)
    : null;
  const performanceContext = useMemo(() => ({
    ...createDefaultPerformanceContext(),
    ...(scorecardContext || {}),
    periodType: period || scorecardContext?.periodType || 'monthly'
  }), [period, scorecardContext]);
  const periodStart = useMemo(
    () => derivePeriodStartFromContext(performanceContext),
    [performanceContext]
  );
  const scorecardLocationCode = managerData?.locationCode && managerData.locationCode !== 'ALL'
    ? managerData.locationCode
    : '';

  useEffect(() => {
    setScreenScorecardViewMode(period === 'yearly' ? 'ytd' : 'mtd');
  }, [period]);

  useEffect(() => {
    if (Number.isFinite(Number(scorecardCardLimit))) {
      setScorecardCardLimitState(Math.max(1, Math.min(8, Math.trunc(Number(scorecardCardLimit)))));
    }
  }, [scorecardCardLimit]);

  useEffect(() => {
    const handleLayoutUpdated = () => {
      if (!Number.isFinite(Number(scorecardCardLimit))) {
        setScorecardCardLimitState(getStoredScorecardCardLimit());
      }
    };

    window.addEventListener(DIRECTOR_SCORECARD_LAYOUT_UPDATED_EVENT, handleLayoutUpdated);
    return () => window.removeEventListener(DIRECTOR_SCORECARD_LAYOUT_UPDATED_EVENT, handleLayoutUpdated);
  }, [scorecardCardLimit]);

  const trackerUrl = useMemo(() => {
    if (!hasManagerData || !managerData) {
      return null;
    }
    if (isDirectorLocationTotal) {
      const qs = buildQuery({
        periodType: performanceContext.periodType,
        periodStart,
        locationCode: managerData.locationCode && managerData.locationCode !== 'ALL' ? managerData.locationCode : null
      });
      return `/api/tracker/location?${qs}`;
    }
    const qs = buildQuery({
      periodType: performanceContext.periodType,
      periodStart
    });
    return `/api/tracker/manager/${encodeURIComponent(manager)}?${qs}`;
  }, [hasManagerData, isDirectorLocationTotal, manager, managerData, performanceContext.periodType, periodStart]);

  const trackerRequestOptions = useMemo(
    () => ({ timeoutMs: isDirectorLocationTotal ? 45000 : 30000 }),
    [isDirectorLocationTotal]
  );

  const {
    data: trackerData,
    isLoading: trackerLoading,
    error: trackerFetchError,
    refetch: refetchTracker
  } = useFetch(trackerUrl, { requestOptions: trackerRequestOptions, deps: [dataVersion] });

  useEffect(() => {
    if (!trackerUrl) {
      setTrackerPayload(null);
      setTrackerError('');
      setIsTrackerLoading(false);
      setLastTrackerRefreshAt(null);
      return;
    }
    setIsTrackerLoading(trackerLoading);
    if (trackerFetchError) {
      setTrackerError(trackerFetchError);
      setTrackerPayload(null);
      return;
    }
    if (trackerData !== undefined && trackerData !== null) {
      setTrackerPayload(trackerData);
      setLastTrackerRefreshAt(new Date());
      setTrackerError('');
    }
  }, [trackerUrl, trackerData, trackerLoading, trackerFetchError]);

  useEffect(() => {
    const handler = () => refetchTracker();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
  }, [refetchTracker]);

  const { data: kpiRows, error: kpiError, refetch: refetchKpis } = useFetch('/api/kpis?includeInactive=true');

  useEffect(() => {
    if (kpiError) {
      setKpis([]);
      return;
    }
    if (Array.isArray(kpiRows)) {
      setKpis(kpiRows);
    }
  }, [kpiRows, kpiError]);

  useEffect(() => {
    const handler = () => refetchKpis();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
  }, [refetchKpis]);

  const attendanceSummaryUrl = useMemo(() => {
    if (!hasManagerData || !managerData) {
      return null;
    }
    const { startDate, endDate } = deriveDateRangeFromPeriodContext(performanceContext.periodType, periodStart);
    if (!startDate || !endDate) {
      return null;
    }
    let scopedLocationCode = null;
    let scopedManagerId = null;
    if (isDirectorLocationTotal) {
      scopedLocationCode = managerData.locationCode && managerData.locationCode !== 'ALL' ? managerData.locationCode : null;
    } else if (managerData.managerDbId) {
      scopedManagerId = managerData.managerDbId;
    }
    const qs = buildQuery({
      startDate,
      endDate,
      locationCode: scopedLocationCode,
      managerId: scopedManagerId
    });
    return `/api/events/attendance/summary?${qs}`;
  }, [hasManagerData, isDirectorLocationTotal, managerData, performanceContext.periodType, periodStart]);

  const attendanceRequestOptions = useMemo(
    () => ({ timeoutMs: isDirectorLocationTotal ? 30000 : 18000 }),
    [isDirectorLocationTotal]
  );

  const {
    data: attendanceData,
    isLoading: attendanceLoading,
    error: attendanceFetchError,
    refetch: refetchAttendanceSummary
  } = useFetch(attendanceSummaryUrl, { requestOptions: attendanceRequestOptions, deps: [dataVersion] });

  useEffect(() => {
    if (!attendanceSummaryUrl) {
      setAttendanceSummary(null);
      setAttendanceError('');
      setIsAttendanceLoading(false);
      return;
    }
    setIsAttendanceLoading(attendanceLoading);
    if (attendanceFetchError) {
      setAttendanceError(attendanceFetchError);
      setAttendanceSummary(null);
      return;
    }
    if (attendanceData !== undefined) {
      setAttendanceSummary(attendanceData);
      setAttendanceError('');
    }
  }, [attendanceSummaryUrl, attendanceData, attendanceLoading, attendanceFetchError]);

  useEffect(() => {
    const handler = () => refetchAttendanceSummary();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
  }, [refetchAttendanceSummary]);

  useEffect(() => {
    if (variant !== 'director_scorecard' || !hasManagerData || !managerData) {
      setScreenScorecardData(null);
      setScreenScorecardError('');
      return undefined;
    }

    let isMounted = true;

    const loadScreenScorecardData = async () => {
      setScreenScorecardError('');

      try {
        const monthlyContext = {
          ...createDefaultPerformanceContext(),
          ...(scorecardContext || {}),
          periodType: 'monthly'
        };
        const yearlyContext = {
          ...createDefaultPerformanceContext(),
          ...(scorecardContext || {}),
          periodType: 'yearly'
        };
        const monthlyPeriodStart = derivePeriodStartFromContext(monthlyContext);
        const yearlyPeriodStart = derivePeriodStartFromContext(yearlyContext);

        const scorecardActualScope = buildScorecardActualScope({
          isDirectorLocationTotal,
          manager,
          managerData,
          locationCode: scorecardLocationCode
        });
        const hasScorecardActualScope = Object.keys(scorecardActualScope).length > 0;
        const scorecardActualPromises = hasScorecardActualScope
          ? [
            apiRequest(`/api/actuals?${buildQuery({
              ...scorecardActualScope,
              periodType: 'monthly',
              periodStart: monthlyPeriodStart
            })}`),
            apiRequest(`/api/actuals?${buildQuery({
              ...scorecardActualScope,
              periodType: 'yearly',
              periodStart: yearlyPeriodStart
            })}`),
            apiRequest(`/api/actuals?${buildQuery({
              ...scorecardActualScope,
              periodType: 'monthly',
              actualYear: Number(yearlyContext.year)
            })}`)
          ]
          : [
            Promise.resolve([]),
            Promise.resolve([]),
            Promise.resolve([])
          ];

        const [
          monthlyTrackerPayload,
          yearlyTrackerPayload,
          approvalPayload,
          locations,
          monthlyActualRows,
          yearlyExactActualRows,
          yearlyMonthlyFallbackRows
        ] = await Promise.all([
          apiRequest(buildTrackerRequestPath({
            isDirectorLocationTotal,
            manager,
            managerData,
            periodType: 'monthly',
            periodStart: monthlyPeriodStart
          }), {
            timeoutMs: isDirectorLocationTotal ? 45000 : 30000
          }),
          apiRequest(buildTrackerRequestPath({
            isDirectorLocationTotal,
            manager,
            managerData,
            periodType: 'yearly',
            periodStart: yearlyPeriodStart
          }), {
            timeoutMs: isDirectorLocationTotal ? 45000 : 30000
          }),
          scorecardLocationCode
            ? apiRequest(`/api/approvals?${buildQuery({
              locationCode: scorecardLocationCode,
              status: 'pending',
              limit: 250
            })}`)
            : Promise.resolve({ groups: [] }),
          apiRequest('/api/locations'),
          ...scorecardActualPromises
        ]);

        if (!isMounted) {
          return;
        }

        const yearlyExactKeys = new Set(
          (yearlyExactActualRows || []).map((actual) => `${actual.managerId}:${actual.kpiDefinitionId}`)
        );
        const yearlyActualRows = [
          ...(yearlyExactActualRows || []),
          ...(yearlyMonthlyFallbackRows || []).filter((actual) => !yearlyExactKeys.has(`${actual.managerId}:${actual.kpiDefinitionId}`))
        ];

        const locationOwner = locations.find((location) => location.code === scorecardLocationCode)?.primaryDirectorOwner || null;
        const approvalGroups = approvalPayload?.groups || [];
        const supportCounts = buildScorecardSupportCounts(approvalGroups);

        // Auto-switch to YTD when MTD has no actuals but YTD does, so users
        // who enter yearly actuals see results without having to manually switch.
        const mtdHasActuals = (monthlyActualRows || []).length > 0 || (monthlyTrackerPayload?.metrics || []).some(m => m.actual !== null && m.actual !== undefined);
        const ytdHasActuals = yearlyActualRows.length > 0 || (yearlyTrackerPayload?.metrics || []).some(m => m.actual !== null && m.actual !== undefined);
        if (!mtdHasActuals && ytdHasActuals) {
          setScreenScorecardViewMode('ytd');
        }

        const monthlyView = buildScorecardRows({
          metrics: monthlyTrackerPayload?.metrics || [],
          alternateMetrics: yearlyTrackerPayload?.metrics || [],
          kpis,
          approvalGroups,
          actualRows: monthlyActualRows || [],
          locationOwner,
          locationCode: scorecardLocationCode || managerData.locationCode || '',
          locationName: managerData.department || managerData.locationName || managerData.locationCode || '',
          managerId: managerData.managerDbId || managerData.id || null
        });
        const yearlyView = buildScorecardRows({
          metrics: yearlyTrackerPayload?.metrics || [],
          alternateMetrics: monthlyTrackerPayload?.metrics || [],
          kpis,
          approvalGroups,
          actualRows: yearlyActualRows,
          locationOwner,
          locationCode: scorecardLocationCode || managerData.locationCode || '',
          locationName: managerData.department || managerData.locationName || managerData.locationCode || '',
          managerId: managerData.managerDbId || managerData.id || null
        });

        setScreenScorecardData({
          locationOwner,
          supportCounts,
          views: {
            mtd: {
              periodType: 'monthly',
              summary: {
                ...(monthlyTrackerPayload?.summary || {}),
                ...(monthlyView.summary || {})
              },
              rows: monthlyView.rows,
              insights: monthlyView.insights
            },
            ytd: {
              periodType: 'yearly',
              summary: {
                ...(yearlyTrackerPayload?.summary || {}),
                ...(yearlyView.summary || {})
              },
              rows: yearlyView.rows,
              insights: yearlyView.insights
            }
          }
        });
      } catch (error) {
        if (isMounted) {
          setScreenScorecardError(error.message || 'Unable to load scorecard context.');
          setScreenScorecardData(null);
        }
      }
    };

    loadScreenScorecardData();

    const handleDirectorDataUpdated = () => {
      loadScreenScorecardData();
    };

    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    return () => {
      isMounted = false;
      window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    };
  }, [dataVersion, hasManagerData, isDirectorLocationTotal, kpis, manager, managerData, scorecardContext, scorecardLocationCode, variant]);

  const baseRollups = useMemo(() => (
    managerData ? calculateRollups(managerData, period) : null
  ), [managerData, period]);
  const trackerMetrics = useMemo(() => trackerPayload?.metrics || [], [trackerPayload]);
  const rollups = useMemo(() => {
    if (!baseRollups) {
      return null;
    }

    return {
      ...baseRollups,
      kpis: buildTrackerKpiMap(trackerMetrics),
      atWorkRate: Number(attendanceSummary?.atWorkRate || 0),
      totalPresent: attendanceSummary?.totals?.present || 0,
      totalAbsent: attendanceSummary?.totals?.absent || 0,
      totalLate: attendanceSummary?.totals?.late || 0,
      totalSick: attendanceSummary?.totals?.sick || 0,
      totalVacation: attendanceSummary?.totals?.vacation || 0,
      eligibleWorkdays: attendanceSummary?.totals?.eligibleWorkdays || 0,
      plannedLeaveDays: attendanceSummary?.totals?.plannedLeaveDays || 0,
      unplannedAbsenceDays: attendanceSummary?.totals?.unplannedAbsenceDays || 0,
      atWorkDays: attendanceSummary?.totals?.atWorkDays || 0,
      overallScore: trackerPayload?.summary?.overallScore ?? baseRollups.overallScore
    };
  }, [attendanceSummary, baseRollups, trackerMetrics, trackerPayload]);
  const kpiData = useMemo(() => buildSnapshotChartData(trackerMetrics), [trackerMetrics]);

  // ----- Manager Scorecard v2 — coaching insights -----
  // All four memos derive from already-loaded payloads (screenScorecardData
  // rows, attendanceSummary, trackerPayload.summary). Zero new fetches.
  const scorecardRows = useMemo(() => {
    if (!screenScorecardData) return [];
    const view = screenScorecardData.views?.[screenScorecardViewMode];
    return Array.isArray(view?.rows) ? view.rows : [];
  }, [screenScorecardData, screenScorecardViewMode]);

  const goalProposalKpiOptions = useMemo(
    () => scorecardRows.filter(row => row.kpiKey),
    [scorecardRows]
  );

  const selectedGoalProposalRow = useMemo(
    () => goalProposalKpiOptions.find(row => row.kpiKey === goalProposalKpiKey) || goalProposalKpiOptions[0] || null,
    [goalProposalKpiKey, goalProposalKpiOptions]
  );

  const openGoalProposalModal = () => {
    const firstRow = goalProposalKpiOptions[0] || null;
    setGoalProposalKpiKey(firstRow?.kpiKey || '');
    setGoalProposalValue('');
    setGoalProposalReason('');
    setGoalProposalError('');
    setGoalProposalBanner('');
    setGoalProposalModalOpen(true);
  };

  const closeGoalProposalModal = () => {
    if (isSubmittingGoalProposal) return;
    setGoalProposalModalOpen(false);
    setGoalProposalError('');
  };

  const submitGoalProposal = async () => {
    const row = selectedGoalProposalRow;
    if (!row) {
      setGoalProposalError('Choose a KPI to propose a goal change.');
      return;
    }
    const proposedGoal = Number(goalProposalValue);
    if (!Number.isFinite(proposedGoal)) {
      setGoalProposalError('Enter a valid proposed goal value.');
      return;
    }

    setIsSubmittingGoalProposal(true);
    setGoalProposalError('');
    setGoalProposalBanner('');
    try {
      await apiRequest('/api/goal-proposals', {
        method: 'POST',
        body: JSON.stringify({
          locationCode: scorecardLocationCode || managerData?.locationCode,
          kpiKey: row.kpiKey,
          goalPeriodType: performanceContext.periodType,
          periodStart,
          goalYear: performanceContext.year,
          goalMonth: performanceContext.periodType === 'monthly' ? performanceContext.month : null,
          goalQuarter: performanceContext.periodType === 'quarterly' ? performanceContext.quarter : null,
          goalWeek: performanceContext.periodType === 'weekly' ? performanceContext.week : null,
          goalValue: proposedGoal,
          reason: goalProposalReason,
          managerId: managerData?.managerDbId || managerData?.id || null
        })
      });
      setGoalProposalBanner('Goal proposal submitted for director review.');
      window.dispatchEvent(new CustomEvent(DIRECTOR_DATA_UPDATED_EVENT, {
        detail: { source: 'goal-proposal', locationCode: scorecardLocationCode || managerData?.locationCode }
      }));
      await refetchTracker?.();
    } catch (requestError) {
      setGoalProposalError(requestError.message || 'Unable to submit goal proposal.');
    } finally {
      setIsSubmittingGoalProposal(false);
    }
  };

  const drivers = useMemo(() => pickDrivers(scorecardRows), [scorecardRows]);

  const headlineNarrative = useMemo(() => buildHeadlineNarrative({
    summary: trackerPayload?.summary || null,
    drivers,
    attendanceSummary,
    managerName: managerData?.name || ''
  }), [trackerPayload?.summary, drivers, attendanceSummary, managerData?.name]);

  const riskPatterns = useMemo(() => buildRiskPatterns({
    rows: scorecardRows,
    attendanceSummary,
    supportCounts: screenScorecardData?.supportCounts || null
  }), [scorecardRows, attendanceSummary, screenScorecardData?.supportCounts]);

  const coachingTopics = useMemo(
    () => buildCoachingTopics({ risks: riskPatterns, drivers }),
    [riskPatterns, drivers]
  );

  const refreshLabel = useMemo(() => {
    if (!lastTrackerRefreshAt) {
      return 'Waiting for first live load';
    }

    return lastTrackerRefreshAt.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit'
    });
  }, [lastTrackerRefreshAt]);

  if (!hasManagerData) {
    return <div className="dashboard-error">No manager data available.</div>;
  }

  if (!managerData) {
    return <div className="dashboard-error">Manager data not found</div>;
  }

  if (!rollups) {
    return <div className="dashboard-error">Scorecard data is not available for this selection.</div>;
  }

  const loadingLabel = isDirectorLocationTotal
    ? `Loading scorecard for ${managerData.name || 'selected location'}...`
    : `Loading scorecard for ${managerData.name || 'selected manager'}...`;

  if (variant === 'director_scorecard') {
    return (
      <div className="dashboard dashboard-executive">
        <div className="dashboard-scorecard-topline" aria-live="polite">
          <div className="dashboard-scorecard-topline-main">
            {onNavigate ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onNavigate('/director-dashboard')}
                className="dashboard-scorecard-back-btn"
              >
                ← Director Dashboard
              </Button>
            ) : null}
            <div className="dashboard-scorecard-topline-copy">
              <div className="dashboard-scorecard-person">
                {!isDirectorLocationTotal ? (
                  <Avatar
                    identifier={managerData.userId || managerData.managerDbId || managerData.id || managerData.name}
                    name={managerData.name}
                    size="lg"
                    userId={managerData.userId}
                  />
                ) : null}
                <div>
                  <strong>{isDirectorLocationTotal ? 'Location Performance' : 'Performance Scorecard'}</strong>
                  <span>
                    {isTrackerLoading
                      ? 'Refreshing live performance'
                      : trackerError
                        ? 'Live refresh needs attention'
                        : `Last loaded ${refreshLabel}`}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="dashboard-scorecard-topline-meta">
            <span className="dashboard-scorecard-chip">{period === 'yearly' ? 'Yearly View' : 'Monthly View'}</span>
            <span className="dashboard-scorecard-chip">{managerData.locationCode || 'Location rollup'}</span>
            <span className="dashboard-scorecard-chip full-text-value">{managerData.name}</span>
            {!isDirectorLocationTotal ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={openGoalProposalModal}
                disabled={!goalProposalKpiOptions.length}
              >
                Propose Goal Change
              </Button>
            ) : null}
          </div>
        </div>

        {goalProposalModalOpen ? (
          <div className="dashboard-goal-proposal-backdrop" role="presentation">
            <div className="dashboard-goal-proposal-modal" role="dialog" aria-modal="true" aria-labelledby="dashboard-goal-proposal-title">
              <div className="dashboard-goal-proposal-head">
                <div>
                  <span>Manager request</span>
                  <h3 id="dashboard-goal-proposal-title">Propose Goal Change</h3>
                </div>
                <Button size="sm" variant="secondary" onClick={closeGoalProposalModal} disabled={isSubmittingGoalProposal}>
                  Close
                </Button>
              </div>
              {goalProposalError ? <div className="dashboard-goal-proposal-error">{goalProposalError}</div> : null}
              {goalProposalBanner ? <div className="dashboard-goal-proposal-success">{goalProposalBanner}</div> : null}
              <div className="dashboard-goal-proposal-context">
                <span>Manager · {managerData.name}</span>
                <span>Location · {scorecardLocationCode || managerData.locationCode}</span>
                <span>Period · {periodStart || (period === 'yearly' ? 'Yearly' : 'Monthly')}</span>
              </div>
              <label className="dashboard-goal-proposal-field">
                KPI
                <select
                  value={selectedGoalProposalRow?.kpiKey || ''}
                  onChange={(event) => setGoalProposalKpiKey(event.target.value)}
                  disabled={isSubmittingGoalProposal}
                >
                  {goalProposalKpiOptions.map(row => (
                    <option key={row.kpiKey} value={row.kpiKey}>{row.label || row.kpiLabel || row.kpiKey}</option>
                  ))}
                </select>
              </label>
              <div className="dashboard-goal-proposal-grid">
                <label className="dashboard-goal-proposal-field">
                  Current goal
                  <input
                    type="text"
                    value={Number.isFinite(Number(selectedGoalProposalRow?.target)) ? selectedGoalProposalRow.target : 'No current goal'}
                    readOnly
                  />
                </label>
                <label className="dashboard-goal-proposal-field">
                  Proposed goal
                  <input
                    type="number"
                    step="0.01"
                    value={goalProposalValue}
                    onChange={(event) => setGoalProposalValue(event.target.value)}
                    disabled={isSubmittingGoalProposal}
                    placeholder="Enter proposed target"
                  />
                </label>
              </div>
              <label className="dashboard-goal-proposal-field">
                Reason
                <textarea
                  value={goalProposalReason}
                  onChange={(event) => setGoalProposalReason(event.target.value)}
                  disabled={isSubmittingGoalProposal}
                  placeholder="What changed, and why should this goal move?"
                />
              </label>
              <div className="dashboard-goal-proposal-actions">
                <Button variant="secondary" onClick={closeGoalProposalModal} disabled={isSubmittingGoalProposal}>
                  Cancel
                </Button>
                <Button
                  onClick={submitGoalProposal}
                  loading={isSubmittingGoalProposal}
                  loadingLabel="Submitting…"
                  disabled={!goalProposalKpiOptions.length}
                >
                  Submit Proposal
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {isTrackerLoading ? (
          <DashboardLoadingSkeleton label={loadingLabel} />
        ) : null}

        {/* Manager Scorecard v2 — coaching surface above the KPI grid.
            Replaces the 4-card executive overview with: a one-sentence
            narrative, three driver cards (biggest drag / gain / most
            improved), and a 2-column risk-patterns + coaching-topics
            panel. All data already loaded; no new fetches. */}
        {(scorecardRows.length || trackerPayload?.summary) ? (
          <section className="scorecard-v2 scorecard-v2-coaching" aria-label="Manager coaching insights">
            <p className="scorecard-v2-narrative" aria-live="polite" aria-atomic="true">
              {headlineNarrative}
            </p>

            <div className="scorecard-v2-drivers" role="group" aria-label="Score drivers">
              <DriverCard
                eyebrow="Biggest drag"
                row={drivers.biggestDrag}
                emptyText="No KPI is dragging the score this period."
                tone="behind"
                onDrilldown={onManagerDrilldown}
                manager={managerData}
              />
              <DriverCard
                eyebrow="Biggest gain"
                row={drivers.biggestGain}
                emptyText="No KPI is exceeding plan this period."
                tone="good"
                onDrilldown={onManagerDrilldown}
                manager={managerData}
              />
              <DriverCard
                eyebrow="Most improved"
                row={drivers.mostImproved}
                emptyText="No KPI shows period-over-period improvement yet."
                tone="watch"
                showDelta
                onDrilldown={onManagerDrilldown}
                manager={managerData}
              />
            </div>

            <div className="scorecard-v2-coaching-row">
              <div className="scorecard-v2-panel" aria-label="Risk patterns">
                <div className="scorecard-v2-panel-head">
                  <h3 className="scorecard-v2-panel-title">Risk patterns</h3>
                  <p className="scorecard-v2-panel-subtitle">Signals worth flagging in the next 1:1.</p>
                </div>
                {riskPatterns.length ? (
                  <ul className="scorecard-v2-list">
                    {riskPatterns.map((risk) => (
                      <li key={risk.id} className="scorecard-v2-row">
                        <StatusDot tone={risk.tone} className="scorecard-v2-dot" aria-hidden />
                        <div className="scorecard-v2-body">
                          <div className="scorecard-v2-title">{risk.title}</div>
                          {risk.subtitle ? (
                            <div className="scorecard-v2-sub">{risk.subtitle}</div>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <EmptyState
                    icon="✓"
                    headline="No risk patterns detected"
                    body="This manager is steady — no persistent declines, attendance clusters, or stuck workflow."
                    compact
                  />
                )}
              </div>

              <div className="scorecard-v2-panel" aria-label="Suggested 1:1 topics">
                <div className="scorecard-v2-panel-head">
                  <h3 className="scorecard-v2-panel-title">Suggested 1:1 topics</h3>
                  <p className="scorecard-v2-panel-subtitle">Concrete things to bring up next.</p>
                </div>
                <ul className="scorecard-v2-list">
                  {coachingTopics.map((topic) => (
                    <li key={topic.id} className="scorecard-v2-row">
                      <StatusDot tone={topic.tone} className="scorecard-v2-dot" aria-hidden />
                      <div className="scorecard-v2-body">
                        <div className="scorecard-v2-title">{topic.title}</div>
                        {topic.subtitle ? (
                          <div className="scorecard-v2-sub">{topic.subtitle}</div>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        ) : null}

        {/* Coaching & Accountability v1 — log + inline editor + action
            items. Only rendered for individual managers, not the
            location-rollup pseudo-manager. */}
        {!isDirectorLocationTotal && managerData && Number.isFinite(Number(managerData.managerDbId || managerData.id)) ? (
          <CoachingLog
            manager={managerData}
            currentUser={currentUser}
            period={period}
          />
        ) : null}

        {/* Batch 1 / B15 — the legacy 4-card "classic KPI overview" accordion
            was kept after the v2 scorecard refactor as a transitional
            comfort surface. It is now consistently redundant with the v2
            driver cards + KPI grid; removed for v1 client readiness. */}

        <Scorecard
          manager={managerData}
          rollups={rollups}
          period={period}
          displayMode="screen"
          title=""
          screenScorecardData={screenScorecardData}
          screenScorecardError={screenScorecardError}
          screenScorecardViewMode={screenScorecardViewMode}
          onScreenScorecardViewModeChange={setScreenScorecardViewMode}
          onManagerDrilldown={onManagerDrilldown}
          scorecardCardLimit={scorecardCardLimitState}
        />

        <details className="dashboard-executive-support">
          <summary>Attendance & leave context</summary>
          <div className="dashboard-executive-secondary">
            {attendanceError ? (
              <div className="manager-kpi-dashboard-error">{attendanceError}</div>
            ) : isAttendanceLoading ? (
              <AttendanceSummarySkeleton />
            ) : (
              <AttendanceStatus summary={attendanceSummary} />
            )}
            <VacationTracker data={managerData.vacations} />
          </div>
        </details>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-status-row" aria-live="polite">
        <div className="dashboard-status-pill">
          <strong>Live Scorecard</strong>
          <span>
            {isTrackerLoading
              ? 'Refreshing now'
              : trackerError
                ? 'Refresh needs attention'
                : 'Current'}
          </span>
        </div>
        <div className="dashboard-status-meta">
          <span>Last loaded: {refreshLabel}</span>
          <span>{isDirectorLocationTotal ? 'Location rollup view' : 'Manager drilldown view'}</span>
        </div>
      </div>

      {isTrackerLoading && (
        <DashboardLoadingSkeleton label={loadingLabel} />
      )}

      <ManagerKPIDashboard
        key={`${manager}-${period}-${dataVersion}`}
        metrics={trackerMetrics}
        period={period}
        isLoading={isTrackerLoading}
        error={trackerError}
        title={isDirectorLocationTotal ? 'Location Performance Scorecard' : 'Manager Performance Scorecard'}
        badge={isDirectorLocationTotal ? 'Location Rollup' : `${trackerPayload?.summary?.metricCount || trackerMetrics.length} Metrics`}
        emptyMessage="No live goal and actual data found for this selection."
      />

      <div className="dashboard-grid">
        <div className="charts-section">
          <div className="section-header">
            <h2><i className="fas fa-chart-line"></i> Performance Trends</h2>
          </div>
          <KPICharts kpiData={kpiData} period={period} trackerMetrics={trackerMetrics} />
        </div>

        <div className="side-panel">
          {attendanceError ? (
            <div className="manager-kpi-dashboard-error">{attendanceError}</div>
          ) : isAttendanceLoading ? (
            <AttendanceSummarySkeleton />
          ) : (
            <AttendanceStatus summary={attendanceSummary} />
          )}
          <VacationTracker data={managerData.vacations} />
        </div>
      </div>

      <Scorecard 
        manager={managerData} 
        rollups={rollups}
        period={period}
      />
    </div>
  );
}

/**
 * v2 driver card — small KpiCard wrapper used by the three "biggest drag /
 * gain / most improved" tiles above the scorecard table. Pure
 * presentational; the row argument comes from screenScorecardData and
 * is allowed to be null (renders an empty state).
 */
function DriverCard({ eyebrow, row, emptyText, tone = 'neutral', showDelta = false, onDrilldown = null, manager = null }) {
  if (!row) {
    return (
      <div className="scorecard-v2-driver scorecard-v2-driver-empty">
        <div className="scorecard-v2-driver-eyebrow">{eyebrow}</div>
        <div className="scorecard-v2-driver-empty-body">{emptyText}</div>
      </div>
    );
  }

  const delta = showDelta ? computePeriodDelta(row) : null;
  const handler = (typeof onDrilldown === 'function')
    ? () => {
      const targetManagerId = row.managerId || manager?.managerDbId || manager?.id;
      if (targetManagerId) {
        onDrilldown(targetManagerId, row);
      }
    }
    : null;

  return (
    <div className={`scorecard-v2-driver scorecard-v2-driver--${tone}`}>
      <div className="scorecard-v2-driver-eyebrow">{eyebrow}</div>
      <KpiCardUI
        variant="highlight"
        label={row.label}
        value={Number.isFinite(row.achievementPercent)
          ? `${Math.round(row.achievementPercent)}% of goal`
          : Number.isFinite(row.result)
            ? 'Target needed'
            : 'Pending'}
        metaLabel={delta?.label || row.statusLabel || ''}
        status={tone}
        onClick={handler}
      />
    </div>
  );
}

function DashboardLoadingSkeleton({ label }) {
  return (
    <div className="dashboard-loading-banner dashboard-loading-skeleton" role="status" aria-live="polite" aria-label={label}>
      <div>
        <SkeletonBlock width={220} height={14} />
        <SkeletonBlock width={320} height={12} style={{ marginTop: 8 }} />
      </div>
      <SkeletonBlock width={96} height={28} radius="var(--radius-pill)" />
    </div>
  );
}

function AttendanceSummarySkeleton() {
  return (
    <div className="manager-kpi-dashboard-loading manager-kpi-dashboard-loading-skeleton" role="status" aria-live="polite" aria-label="Loading attendance summary">
      <SkeletonBlock width="46%" height={14} />
      <SkeletonBlock width="72%" height={28} radius="var(--radius-10)" />
      <SkeletonBlock width="60%" height={12} />
    </div>
  );
}

export {
  buildScorecardActualScope,
  buildScorecardRows,
  computeScorecardAchievementPercent
};

export default Dashboard;
